import { OTP_DELAY_MINUTES, 
    MAXIMUM_TAT_MINUTES, 
    FLIGHT_DATES,
} from 'state/constants';
import { capitalizeFirstLetter, 
    getSortableValue,
    formatXValue,
} from 'state/helpers';


const sortDataByXAxis = (chartData, xAxisName) => {
    if (!Array.isArray(chartData)) {
        throw new TypeError('Expected chart data to be an array.');
    }

    if (!xAxisName || typeof xAxisName !== 'string') {
        throw new TypeError('xAxisName should be a non-empty string.');
    }

    const sortedData = chartData
        .sort((a, b) => {
            const valueA = getSortableValue(a[xAxisName] || a['x']);
            const valueB = getSortableValue(b[xAxisName] || b['x']);

            if (typeof valueA === "string" && typeof valueB === "string") {
                return valueA.localeCompare(valueB);
            }

            return valueA - valueB;
        })
        .map((item) => ({
            ...item,
            [xAxisName]: formatXValue(item[xAxisName] || item['x']), 
        }));

    return sortedData;
}

/* ------------------- Flight Charts --------------------*/
/* Flights */
export function countValuesByField(
    flights, 
    fieldName, 
    groupBy, 
    chartType, 
    otpDelayMinutes = OTP_DELAY_MINUTES
) {
    const conditions = {
        'tat': (time) => time && time < MAXIMUM_TAT_MINUTES,
        'scheduleTat': (time) => time && time < MAXIMUM_TAT_MINUTES,
        'estimatedTat': (time) => time && time < MAXIMUM_TAT_MINUTES,
        'actualTat': (time) => time && time < MAXIMUM_TAT_MINUTES,
        'tatDiff': (flight) => flight.actualTat > 0 && flight.actualTat < MAXIMUM_TAT_MINUTES, 
        'actualEstimatedTatDiff': (flight) => flight.actualEstimatedTatDiff > 0 && flight.actualEstimatedTatDiff < MAXIMUM_TAT_MINUTES, 
        'taxiOut': (time) => time && time > 0,
        'taxiIn': (time) => time && time > 0,
        'btDiff': (flight) => flight.atd && flight.ata,
        'airMinutes': (flight) => flight.takeOff && flight.touchDown,
        'blockMinutes': () => true, 
        'actualBlockMinutes': () => true, 
        'scheduleBlockMinutes': () => true, 
        'estimatedBlockMinutes': () => true, 
        'blockHours': () => true, 
        'depDelaysTime': (time) => time && time > otpDelayMinutes,
        'arrDelaysTime': (time) => time && time > otpDelayMinutes,
    };

    const flightObjects = [
        'tatDiff', 
        'actualEstimatedTatDiff', 
        'btDiff', 
        'airMinutes'
    ];

    if (!Object.keys(conditions).includes(fieldName)) {
        throw new Error("Invalid field name.");
    }
        
    const objValues = flights.reduce((acc, flight) => {
        const key = flight[groupBy] || 'N/A';
        
        const dep = flight.dep;
        const arr = flight.arr;
        const value = flight[fieldName];

        if (dep !== arr) {
            const isValid = flightObjects.includes(fieldName) 
                ? conditions[fieldName](flight) 
                : conditions[fieldName](value);

            if (isValid) {
                if (!acc[key]) {
                    acc[key] = { totalFlt: 0, totalTime: 0 };
                }
                acc[key].totalFlt += 1;
                acc[key].totalTime += value;
            }
        }
        
        return acc;
    }, {});

    if (Object.keys(objValues).length === 0) return [];

    // Convert accumulated data to the required format
    const arrValues = Object.entries(objValues)
        .map(([key, { totalFlt, totalTime }]) => ({
            x: key,
            y: totalFlt > 0 ? parseFloat((totalTime / totalFlt).toFixed(0)) : 0,
        }))
        .sort((a, b) => FLIGHT_DATES.includes(fieldName) 
            ? new Date(a.x) - new Date(b.x)  
            : a.x.localeCompare(b.x)
        ); 

    const totalFlts = Object.values(objValues).reduce((acc, { totalFlt }) => acc + totalFlt, 0);
    const totalTimes = Object.values(objValues).reduce((acc, { totalTime }) => acc + totalTime, 0);
    const average = totalFlts > 0 ? parseFloat((totalTimes / totalFlts).toFixed(1)) : 0;
    const arrAverage = arrValues.map(({ x }) => ({
        x,
        y: average
    }));
    
    const barLineChartData = [        
        { id: capitalizeFirstLetter(fieldName), color: 'hsl(120, 70%, 50%)', data: arrValues },        
        { id: 'Average', color: 'hsl(200, 70%, 50%)', data: arrAverage },
    ];
    
    switch (chartType) {
        case 'bar':
        case 'column':
            return arrValues;        
        case 'pie':            
            return Object.entries(objValues).map(([key, { totalTime }]) => ({
                id: key,
                label: key,
                value: totalTime,
            }));
        case 'barline':
            return barLineChartData;
        default:
            return barLineChartData;            
    }
}

export function countValuesByFields(flights, fieldName1, fieldName2, groupBy, otpDelayMinutes = OTP_DELAY_MINUTES) {
    const conditions = {
        'tat': (time) => time && time < MAXIMUM_TAT_MINUTES,
        'scheduleTat': (time) => time && time < MAXIMUM_TAT_MINUTES,
        'tatDiff': (flight) => flight.actualTat > 0, 
        'taxiOut': (time) => time && time > 0,
        'taxiIn': (time) => time && time > 0,
        'btDiff': (flight) => flight.atd && flight.ata,
        'airMinutes': (flight) => flight.takeOff && flight.touchDown,
        'blockMinutes': () => true, 
        'blockHours': () => true, 
        'depDelaysTime': (time) => time && time > otpDelayMinutes,
        'arrDelaysTime': (time) => time && time > otpDelayMinutes,
        'preArrDelaysTime': (time) => time && time > otpDelayMinutes,
    };

    const flightObjects = ['tatDiff', 'btDiff', 'airMinutes'];

    if (!Object.keys(conditions).includes(fieldName1)) {
        throw new Error("Invalid field name.");
    }
        
    const objValues = flights.reduce((acc, cur) => {
        const key = cur[groupBy] || 'N/A';
        
        const dep = cur.dep;
        const arr = cur.arr;
        const value1 = cur[fieldName1];
        const value2 = cur[fieldName2];

        if (dep !== arr) {
            const isValid = flightObjects.includes(fieldName1) 
                ? conditions[fieldName1](cur) 
                : conditions[fieldName1](value1);

            if (isValid) {
                if (!acc[key]) {
                    acc[key] = { totalFlt: 0, totalTime1: 0, totalTime2: 0 };
                }
                acc[key].totalFlt += 1;
                acc[key].totalTime1 += value1;
                acc[key].totalTime2 += value2;
            }
        }
        
        return acc;
    }, {});

    if (Object.keys(objValues).length === 0) return [];

    // Convert accumulated data to the required format
    const arrValues1 = Object.entries(objValues)
        .map(([key, { totalFlt, totalTime1 }]) => ({
            x: key,
            y: totalFlt > 0 ? parseFloat((totalTime1 / totalFlt).toFixed(0)) : 0,
        }))
        .sort((a, b) => FLIGHT_DATES.includes(fieldName1) 
            ? new Date(a.x) - new Date(b.x)  
            : a.x.localeCompare(b.x)
        ); 

    const totalFlts = Object.values(objValues).reduce((acc, { totalFlt }) => acc + totalFlt, 0);
    const totalTimes1 = Object.values(objValues).reduce((acc, { totalTime1 }) => acc + totalTime1, 0);
    const average1 = totalFlts > 0 ? parseFloat((totalTimes1 / totalFlts).toFixed(1)) : 0;
    const arrAverage1 = arrValues1.map(({ x }) => ({
        x,
        y: average1
    }));

    const arrValues2 = Object.entries(objValues)
        .map(([key, { totalFlt, totalTime2 }]) => ({
            x: key,
            y: totalFlt > 0 ? parseFloat((totalTime2 / totalFlt).toFixed(0)) : 0,
        }))
        .sort((a, b) => FLIGHT_DATES.includes(fieldName1) 
            ? new Date(a.x) - new Date(b.x)  
            : a.x.localeCompare(b.x)
        ); 

    const totalTimes2 = Object.values(objValues).reduce((acc, { totalTime2 }) => acc + totalTime2, 0);
    const average2 = totalFlts > 0 ? parseFloat((totalTimes2 / totalFlts).toFixed(1)) : 0;
    const arrAverage2 = arrValues1.map(({ x }) => ({
        x,
        y: average2
    }));
    
    const chartData = [        
        { 
            id: capitalizeFirstLetter(fieldName1), 
            color: 'hsl(120, 70%, 50%)', 
            data: arrValues1,
        },        
        { 
            id: 'DepAverage', 
            color: 'hsl(200, 70%, 50%)', 
            data: arrAverage1,
        },
        { 
            id: capitalizeFirstLetter(fieldName2), 
            color: 'hsl(20, 70%, 50%)',
            data: arrValues2,
            isAllPointsTextVisible: true,
        },        
        { 
            id: 'PreArrAverage', 
            color: 'hsl(60, 70%, 50%)',
            data: arrAverage2,
        },
    ];
    
    return chartData;     
}

export function countValuesByFieldAndDuration(flights, fieldName, groupBy, duration, chartType) {    
    const objValues = flights.reduce((acc, flight) => {
        const key = flight[groupBy] || 'N/A';
        
        const value = flight[fieldName] || 0;
        const dep = flight.dep;
        const arr = flight.arr;
        
        if (dep !== arr) {
            if (!acc[key]) {
                acc[key] = { totalFlt: 0, totalTime: 0 };
            }
            
            acc[key].totalFlt += 1;
            acc[key].totalTime += value;
        }
        
        return acc;
    }, {});

    if (Object.keys(objValues).length === 0) return [];

    // Convert accumulated data to the required format
    const arrValues = Object.entries(objValues)
        .map(([formattedKey, { totalFlt, totalTime }]) => ({
            x: formattedKey,
            y: duration > 0 ? parseFloat(((fieldName ? totalTime : totalFlt) / duration).toFixed(1)) : 0,
        }))
        .sort((a, b) => FLIGHT_DATES.includes(fieldName) 
            ? new Date(a.x) - new Date(b.x)  
            : a.x.localeCompare(b.x)
        ); 
    
    const sumUp = arrValues.reduce((acc, cur) => acc + cur.y, 0);
    const average = arrValues.length > 0 ? parseFloat((sumUp / arrValues.length).toFixed(1)) : 0;
    const arrAverage = arrValues.map(({ x }) => ({
        x,
        y: average
    }));
    
    const barLineChartData = [        
        { id: capitalizeFirstLetter(fieldName), color: 'hsl(120, 70%, 50%)', data: arrValues },        
        { id: 'Average', color: 'hsl(200, 70%, 50%)', data: arrAverage },
    ];

    switch (chartType) {
        case 'bar':
        case 'column':
            return arrValues;        
        case 'pie':            
            return Object.entries(objValues).map(([key, value]) => ({
                id: key,
                label: key,
                value: fieldName ? value.totalTime : value.totalFlt,
            }));
        case 'barline':
            return barLineChartData;
        default:
            return barLineChartData;            
    }
}

export function countFlightsByField(
    flights,
    fieldName,
    chartType,
    legend = 'Flt',
    legendAvg = 'FltAvg',
    yValue = 'count'
) {
    if (!Array.isArray(flights) || flights.length === 0) return [];

    const obj = flights.reduce((acc, flight) => {
        let key = flight[fieldName] || 'N/A';
        if (fieldName === 'cancelledReason') {
            key = key.split(" ")[0]; 
        }

        if (!acc[key]) acc[key] = {};

        // Nếu chưa có accumulator cho field thì khởi tạo
        if (!acc[key][yValue]) acc[key][yValue] = 0;

        if (yValue === 'count') {
            acc[key][yValue] += 1;
        } else {
            acc[key][yValue] += Number(flight[yValue]) || 0;
        }

        return acc;
    }, {});

    if (Object.keys(obj).length === 0) return [];

    // sort theo ngày hoặc alphabet
    const dateFields = new Set(FLIGHT_DATES);
    const sortData = (a, b) =>
        dateFields.has(fieldName)
            ? new Date(a.x) - new Date(b.x)
            : a.x.localeCompare(b.x);

    // Dataset chính
    const arrFlights = Object.entries(obj)
        .map(([key, values]) => ({
            x: key,
            y: values[yValue]
        }))
        .sort(sortData);

    // Tính average
    const total = arrFlights.reduce((acc, cur) => acc + cur.y, 0);
    const avg = parseFloat((total / arrFlights.length).toFixed(1));
    const arrFlightsAverage = arrFlights.map(({ x }) => ({ x, y: avg }));

    const barLineChartData = [
        { id: legend, color: 'hsl(120, 70%, 50%)', data: arrFlights },
        { id: legendAvg, color: 'hsl(200, 70%, 50%)', data: arrFlightsAverage },
    ];

    // Trả về theo chartType
    switch (chartType) {
        case 'bar':
        case 'column':
            return arrFlights;
        case 'pie':
            return arrFlights.map(({ x, y }) => ({
                id: x,
                label: x,
                value: y,
            }));
        case 'barline':
        default:
            return barLineChartData;
    }
}

export function countFlightsStackedBarChart(flights, fieldName) {    
    const objOTP = flights.reduce((acc, flight) => {
        const key = flight[fieldName];
        const delayMinutes = flight.depDelaysTime;
       
       if (!acc[key]) {
            acc[key] = { totalFlights: 0, delayedFlights: 0, ontimeFlights: 0 };
        }
        
        acc[key].totalFlights += 1;
        if (delayMinutes > OTP_DELAY_MINUTES) {
            acc[key].delayedFlights += 1;
        } else {
            acc[key].ontimeFlights += 1;
        }

        return acc;
    }, {});

    if (Object.keys(objOTP).length === 0) return [];
        
    const chartData = Object.entries(objOTP)
        .map(([key, value]) => ({
            x: key,
            Ontime: value.ontimeFlights,
            Delay: value.delayedFlights,
        }));
        
    sortDataByXAxis(chartData, 'x');
    
    return chartData.map((item) => ({
        ...item,
        x: formatXValue(item.x), 
    }));
}

export function countFlightsByFieldStackedBarChart(flights, fieldName) {    
    const objFLT = flights.reduce((acc, flight) => {
        const key = flight[fieldName] || 'N/A';
           
        if (!acc[key]) {
            acc[key] = { totalFlights: 0, scheduledFlights: 0, cancelledFlights: 0 };
        }

        acc[key].totalFlights += 1;
        if (flight.cancelledReason) {
            acc[key].cancelledFlights += 1;
        } else {
            acc[key].scheduledFlights += 1;
        }

        return acc;
    }, {});

    if (Object.keys(objFLT).length === 0) return [];
    
    const chartData = Object.entries(objFLT)
        .map(([key, value]) => ({
            x: key,
            Scheduled: value.scheduledFlights,
            Cancelled: value.cancelledFlights,
        }))
        .sort((a, b) => FLIGHT_DATES.includes(fieldName) 
            ? new Date(a.x) - new Date(b.x)  
            : a.x.localeCompare(b.x)
        ); 
      
    return chartData;
}

export function countFlightsByFieldAndDuration(
    flights, 
    fieldName, 
    groupBy, 
    duration, 
    options = {}
) {    
    const {
        perAircraft = false,
    } = options;

    const objValues = flights.reduce((acc, flight) => {
        const key = flight[groupBy] || 'N/A';
        const dep = flight.dep;
        const arr = flight.arr;
        
        if (dep === arr) return acc;
        
        const value = flight[fieldName];
        if (!acc[key]) {
            acc[key] = {
                totalFlt: 0,
                totalTime: 0,
                aircraftSet: new Set(),
            };
        }

        acc[key].totalFlt += 1;
        acc[key].totalTime += value || 0;

        if (perAircraft && flight['flightReg']) {
            acc[key].aircraftSet.add(flight['flightReg']);
        }

        return acc;
    }, {});

    if (Object.keys(objValues).length === 0) return [];

    const buildSeries = (selector) => {
        return Object.entries(objValues)
            .map(([key, value]) => {
                const aircraftCount = perAircraft
                    ? value.aircraftSet.size || 1
                    : 1;

                const divisor = perAircraft
                    ? aircraftCount * (duration || 1)
                    : (duration || 1);

                const raw = selector(value);
                return {
                    x: key,
                    y: parseFloat((raw / divisor).toFixed(1)),
                };
            })
            .sort((a, b) =>
                FLIGHT_DATES.includes(fieldName)
                    ? new Date(a.x) - new Date(b.x)
                    : a.x.localeCompare(b.x)
            );
    };

    // Convert accumulated data to the required format    
    const arrFlights = buildSeries(v => v.totalFlt);
    const arrValues = buildSeries(v => v.totalTime);

    const calcAverageLine = (arr) => {
        const total = arr.reduce((acc, cur) => acc + cur.y, 0);
        const avg = arr.length
            ? parseFloat((total / arr.length).toFixed(1))
            : 0;

        return arr.map(({ x }) => ({ x, y: avg }));
    };

    const fieldLabel = fieldName === "blockHours" ? "BH" : fieldName;


    const chartData = [
        {
            id: perAircraft ? 'Flt/AC' : 'Flt',
            color: 'hsl(20, 70%, 50%)',
            data: arrFlights,
        },
        {
            id: perAircraft ? 'Flt/AC Avg' : 'FltAvg',
            color: 'hsl(60, 70%, 50%)',
            data: calcAverageLine(arrFlights),
        },
        {
            id: perAircraft
                ? `${capitalizeFirstLetter(fieldLabel)}/AC`
                : capitalizeFirstLetter(fieldLabel),
            color: 'hsl(120, 70%, 50%)',
            data: arrValues,
            isAllPointsTextVisible: true,
        },
        {
            id: perAircraft
                ? `${capitalizeFirstLetter(fieldLabel)}/AC Avg`
                : capitalizeFirstLetter(fieldLabel) + 'Avg',
            color: 'hsl(200, 70%, 50%)',
            data: calcAverageLine(arrValues),
        },
    ];      

    return chartData;
}

export function countFlightsBy2Fields(flights, field1, field2) {    
    if (!Array.isArray(flights) || flights.length === 0) return [];

    const obj = flights.reduce((acc, flight) => {     
        const key = flight[field1] || 'N/A';   
        const field2Value = Number(flight[field2]) || 0;

        if (!acc[key]) {
            acc[key] = { count: 0, sumField2: 0 };
        }
        
        acc[key].count += 1;
        acc[key].sumField2 += field2Value;

        return acc;
    }, {});

    if (Object.keys(obj).length === 0) return [];

    const dateFields = new Set(FLIGHT_DATES);

    const sortData = (a, b) => 
        dateFields.has(field1)
            ? new Date(a.x) - new Date(b.x)  
            : a.x.localeCompare(b.x);

    // Flights count per group
    const arrFlights = Object.entries(obj)
        .map(([key, { count }]) => ({ x: key, y: count }))
        .sort(sortData);

    const totalFlights = arrFlights.reduce((acc, cur) => acc + cur.y, 0);
    const flightsAverage = arrFlights.length > 0 
        ? parseFloat((totalFlights / arrFlights.length).toFixed(0)) 
        : 0;
    const arrFlightsAverage = arrFlights.map(({ x }) => ({ x, y: flightsAverage }));   
    
    // Field2 sum (e.g. block hours)
    const arrField2 = Object.entries(obj)
        .map(([key, { sumField2 }]) => ({ x: key, y: parseFloat(sumField2.toFixed(1)) }))
        .sort(sortData);

    const totalField2 = arrField2.reduce((acc, cur) => acc + cur.y, 0);
    const field2Average = arrField2.length > 0 
        ? parseFloat((totalField2 / arrField2.length).toFixed(1)) 
        : 0;
    const arrField2Average = arrField2.map(({ x }) => ({ x, y: field2Average }));   
    
    const field2Label = field2 === "blockHours" ? "BH" : field2;
    const chartData = [        
        { id: 'Flt', color: 'hsl(220, 75%, 64%)', data: arrFlights },
        { id: 'FltAvg', color: 'hsl(134, 66%, 51%)', data: arrFlightsAverage },
        { id: capitalizeFirstLetter(field2Label), color: 'hsla(339, 54%, 55%, 0.88)', data: arrField2, isAllPointsTextVisible: true },
        { id: `${capitalizeFirstLetter(field2Label)}Avg`, color: 'hsl(56, 61%, 47%)', data: arrField2Average },
    ];

    return chartData;
}

export function countStackedFlightsBy2Fields(flights, field1, field2, valueField = 'count') {
    if (!Array.isArray(flights) || flights.length === 0) return [];

    // helpers
    const toKeyString = v => (v === undefined || v === null) ? 'N/A' : String(v);
    const isIntegerString = s => /^-?\d+$/.test(s);

    const obj = {};               // { key1: { key2: { value } } }
    const allCats = new Set();    // collect all category keys (key2) deterministically
    
    // aggregate
    flights.forEach(flight => {
        const rawKey1 = toKeyString(flight[field1]);
        let rawKey2 = toKeyString(flight[field2]);
        
        // nếu key2 là thuần số (integer-like) thì thêm prefix để tránh JS coi là integer property
        const key2 = isIntegerString(rawKey2) ? ` ${rawKey2}` : rawKey2;

        if (!obj[rawKey1]) obj[rawKey1] = {};
        if (!obj[rawKey1][key2]) obj[rawKey1][key2] = { value: 0 };

        const add = valueField === 'count' ? 1 : (Number(flight[valueField]) || 0);
        obj[rawKey1][key2].value += add;

        allCats.add(key2);
    });

    if (Object.keys(obj).length === 0) return [];

    // chuẩn hóa danh sách categories theo thứ tự determinisitc (dùng localeCompare numeric để xếp natural)
    const catList = Array.from(allCats).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

    // build rows đảm bảo mỗi row có đủ keys (0 nếu không có)
    const rows = Object.keys(obj).map(k1 => {
        const row = { x: k1 };
        catList.forEach(cat => {
            row[cat] = (obj[k1][cat] && typeof obj[k1][cat].value === 'number') ? obj[k1][cat].value : 0;
        });
        return row;
    });

    // sort dữ liệu theo field1: nếu là date -> date compare; nếu numeric -> numeric compare; else string compare
    const dateFields = new Set(FLIGHT_DATES);

    const sortData = (a, b) => {
        const ax = a.x;
        const bx = b.x;

        if (dateFields.has(field1)) {
            return new Date(ax) - new Date(bx);
        }

        const na = Number(ax);
        const nb = Number(bx);
        if (!Number.isNaN(na) && !Number.isNaN(nb)) {
            return na - nb;
        }

        return String(ax).localeCompare(String(bx));
    };

    rows.sort(sortData);

    // format x nếu cần (hàm formatXValue)
    return rows.map(item => ({
        ...item,
        x: typeof formatXValue === 'function' ? formatXValue(item.x) : item.x
    }));
}

export function countBlockTimeStackedBarChart(flights, fieldName) {    
    const obj = flights.reduce((acc, flight) => {
        const key = flight[fieldName] || 'N/A';
           
        const flightStatus = (flight.flightStatus || "").toLowerCase();

        if (flightStatus !== 'arrived') return acc;

        if (!acc[key]) {
            acc[key] = { 
                totalFlights: 0, 
                totalTaxiOut: 0, 
                totalAirMinutes: 0, 
                totalTaxiIn: 0 
            };
        }

        acc[key].totalFlights += 1;
        acc[key].totalTaxiOut += Number(flight.taxiOut) || 0;
        acc[key].totalAirMinutes += Number(flight.airMinutes) || 0;
        acc[key].totalTaxiIn += Number(flight.taxiIn) || 0;
        
        return acc;
    }, {});

    if (Object.keys(obj).length === 0) return [];
    
    const chartData = Object.entries(obj)
        .map(([key, value]) => ({
            x: key,
            TaxiOut: value.totalFlights > 0 ? parseFloat((value.totalTaxiOut/ value.totalFlights).toFixed(0)) : 0,
            AirTime: value.totalFlights > 0 ? parseFloat((value.totalAirMinutes/ value.totalFlights).toFixed(0)) : 0,
            TaxiIn: value.totalFlights > 0 ? parseFloat((value.totalTaxiIn/ value.totalFlights).toFixed(0)) : 0,
        }))
        .sort((a, b) => {
            const dateA = Date.parse(a.x);
            const dateB = Date.parse(b.x);

            if (!isNaN(dateA) && !isNaN(dateB)) {
                return dateA - dateB;
            }
            return a.x.localeCompare(b.x);
        });
      
    return chartData;
}

/* Daily Flights */
export function countBarLineFlightsByFieldAndDuration(flights, fieldName, groupBy, duration) {    
    const objValues = flights.reduce((acc, flight) => {
        const key = flight[groupBy] || 'N/A';

        const value = flight[fieldName];
        const dep = flight.dep;
        const arr = flight.arr;

        if (dep !== arr) {
            if (!acc[key]) {
                acc[key] = { totalFlt: 0, totalTime: 0 };
            }
            acc[key].totalFlt += 1;
            acc[key].totalTime += value;
        }
        
        return acc;
    }, {});

    if (Object.keys(objValues).length === 0) return [];

    // Convert accumulated data to the required format    
    const arrFlights = Object.entries(objValues)
        .map(([key, { totalFlt, totalTime }]) => ({
            x: key,
            y: duration > 0 ? parseFloat((totalFlt / duration)) : 0,
            z: duration > 0 ? parseFloat((totalTime / duration)) : 0,
        }));
        
        sortDataByXAxis(arrFlights, 'x');
    
    return arrFlights.map((item) => ({
        ...item,
        x: formatXValue(item.x), 
    }));
}
/* ---------------- End of Flight Charts ----------------*/

/* -------------------- Crew Charts ---------------------*/
export function countCrewRoutesByField(crewRoutes, groupBy, chartType, days = 1) {
    const homeBases = [...new Set(crewRoutes.map(route => route.dep))].sort();
    const objValues = Object.values(crewRoutes).reduce((acc, cur) => {
        const key = cur[groupBy] ?? 'N/A';
        const dep = cur.dep;
        
        if (groupBy === 'dep' && !homeBases.includes(dep))
            return acc;
        
        if (!acc[key]) {
            acc[key] = { 
                totalRoutes: 0, 
                totalBlockHours: 0, 
            };
               
            homeBases.forEach(base => {
                acc[key][base] = 0; 
            });
        }

        acc[key].totalRoutes += 1; 
        acc[key].totalBlockHours += cur.blockHours; 
        
        if (homeBases.includes(dep)) {
            acc[key][dep] += 1; 
        }
    
        return acc; 
    }, {});
    
    if (Object.keys(objValues).length === 0) return [];
    
    // Sorting function
    const sortData = (a, b) => 
        ['routeDate', 'routeDateLocalBase'].includes(groupBy)
            ? new Date(a.x) - new Date(b.x)  
            : a.x.localeCompare(b.x);

    // Create dataset arrays  
    const arrRoutes = Object.entries(objValues)
        .map(([key, { totalRoutes }]) => ({
            x: key,
            y: totalRoutes / days,
        }))
        .sort(sortData);

    const totalCounts = Object.values(objValues).reduce((acc, cur) => acc + 1, 0);
    const totalAllRoutes = Object.values(objValues).reduce((acc, { totalRoutes }) => acc + totalRoutes, 0);
        
    const routesAverage = totalAllRoutes > 0 ? totalAllRoutes/ totalCounts : 0;
    const arrRoutesAverage = arrRoutes.map(({ x }) => ({
        x,
        y: routesAverage / days,
    }));
    
    const arrBlockHours = Object.entries(objValues).map(([key, { totalBlockHours }]) => ({
        x: key, 
        y: totalBlockHours / days
    })).sort(sortData);

    const totalBlockHours = arrBlockHours.reduce((acc, cur) => acc + cur.y, 0);
    const blockHoursAverage = totalBlockHours / arrBlockHours.length;       
    const arrBlockHoursAverage = arrBlockHours.map(({ x }) => ({ x, y: blockHoursAverage/ days }));   
        
    const barLinesChartData = [        
        { id: 'Routes', color: 'hsl(120, 70%, 50%)', data: arrRoutes },        
        { id: 'RoutesAvg', color: 'hsl(200, 70%, 50%)', data: arrRoutesAverage },
        { id: 'BH', color: 'hsla(300, 34.80%, 74.10%, 0.88)', data: arrBlockHours, isAllPointsTextVisible: true },
        { id: 'BHAvg', color: 'hsl(56, 62.40%, 55.10%)', data: arrBlockHoursAverage },
    ];
    
    const arrRoutesByPort = Object.entries(objValues).map(([key, { totalRoutes, totalBlockHours, ...baseCount }]) => {
        const baseData = {};
    
        homeBases.forEach(base => {
            baseData[base] = (baseCount[base] || 0) / days; 
        });
        
        return {
            x: key,
            ...baseData, 
        };
    }).sort((a, b) => a.x.localeCompare(b.x));
    
    const radialBarChartData = Object.entries(objValues).map(([key, { totalRoutes, totalBlockHours, ...baseCount }]) => {
        const data = [];
    
        homeBases.forEach(base => {            
            const value = {
                x: base,
                y: (baseCount[base] || 0) / days,
            }
            data.push(value)
        });

        return {
            id: key,
            data, 
        };
    });

    switch (chartType) {
        case 'bar':
        case 'column':
            return arrRoutes;
        case 'groupedColumn':
            return arrRoutesByPort;
        case 'pie':            
            return Object.entries(objValues).map(([key, {totalRoutes}]) => ({
                id: key,
                label: key,
                value: totalRoutes,
            }));
        case 'radialbar':
            return radialBarChartData;
        case 'barlines':
            return barLinesChartData;
        default:
            return barLinesChartData;            
    }
}

export function countWeightedCrewRoutesByField(crewRoutes, groupBy, chartType, days = 1) {
    const homeBases = [...new Set(crewRoutes.map(route => route.dep))].sort();
    const objValues = Object.values(crewRoutes).reduce((acc, cur) => {
        const key = cur[groupBy] ?? 'N/A';
        const dep = cur.dep;

        if (groupBy === 'dep' && !homeBases.includes(dep))
            return acc;

        if (!acc[key]) {
            acc[key] = {
                totalRoutes: 0,
                totalBlockHours: 0,
                totalCrewCount: 0,
            };
            homeBases.forEach(base => {
                acc[key][base] = 0;
            });
        }

        acc[key].totalRoutes += 1;
        acc[key].totalBlockHours += cur.blockHours;
        acc[key].totalCrewCount += cur.crewCount || 1;

        if (homeBases.includes(dep)) {
            acc[key][dep] += 1;
        }

        return acc;
    }, {});

    if (Object.keys(objValues).length === 0) return [];

    const sortData = (a, b) =>
        ['routeDate', 'routeDateLocalBase'].includes(groupBy)
            ? new Date(a.x) - new Date(b.x)
            : a.x.localeCompare(b.x);

    const arrRoutes = Object.entries(objValues)
        .map(([key, { totalRoutes }]) => ({ x: key, y: totalRoutes / days }))
        .sort(sortData);

    const totalCounts = Object.values(objValues).length;
    const totalAllRoutes = Object.values(objValues).reduce((acc, { totalRoutes }) => acc + totalRoutes, 0);
    const routesAverage = totalAllRoutes > 0 ? totalAllRoutes / totalCounts : 0;
    const arrRoutesAverage = arrRoutes.map(({ x }) => ({ x, y: routesAverage / days }));

    const arrBlockHours = Object.entries(objValues)
        .map(([key, { totalBlockHours }]) => ({ x: key, y: totalBlockHours / days }))
        .sort(sortData);

    const totalWeightedBH   = Object.values(objValues).reduce((acc, { totalBlockHours, totalCrewCount }) => acc + totalBlockHours * totalCrewCount, 0);
    const totalCrewCountAll = Object.values(objValues).reduce((acc, { totalCrewCount }) => acc + totalCrewCount, 0);
    const blockHoursAverage = totalCrewCountAll > 0 ? totalWeightedBH / totalCrewCountAll : 0;
    const arrBlockHoursAverage = arrBlockHours.map(({ x }) => ({ x, y: blockHoursAverage / days }));

    const barLinesChartData = [
        { id: 'Routes', color: 'hsl(120, 70%, 50%)', data: arrRoutes },
        { id: 'RoutesAvg', color: 'hsl(200, 70%, 50%)', data: arrRoutesAverage },
        { id: 'BH', color: 'hsla(300, 34.80%, 74.10%, 0.88)', data: arrBlockHours, isAllPointsTextVisible: true },
        { id: 'BHAvg', color: 'hsl(56, 62.40%, 55.10%)', data: arrBlockHoursAverage },
    ];

    const arrRoutesByPort = Object.entries(objValues).map(([key, { totalRoutes, totalBlockHours, totalCrewCount, ...baseCount }]) => {
        const baseData = {};
        homeBases.forEach(base => {
            baseData[base] = (baseCount[base] || 0) / days;
        });
        return { x: key, ...baseData };
    }).sort((a, b) => a.x.localeCompare(b.x));

    const radialBarChartData = Object.entries(objValues).map(([key, { totalRoutes, totalBlockHours, totalCrewCount, ...baseCount }]) => {
        const data = [];
        homeBases.forEach(base => {
            data.push({ x: base, y: (baseCount[base] || 0) / days });
        });
        return { id: key, data };
    });

    switch (chartType) {
        case 'bar':
        case 'column':
            return arrRoutes;
        case 'groupedColumn':
            return arrRoutesByPort;
        case 'pie':
            return Object.entries(objValues).map(([key, { totalRoutes }]) => ({
                id: key, label: key, value: totalRoutes,
            }));
        case 'radialbar':
            return radialBarChartData;
        case 'barlines':
            return barLinesChartData;
        default:
            return barLinesChartData;
    }
}

export function countBHCrewRoutesByField(crewRoutes, fieldName, groupBy, chartType) {
    try {
        if (!crewRoutes) {
            return [];
        }

        const conditions = {
            'blockHours': (time) => time && time > 0,
            'dhcTime': (time) => time && time > 0,
        };
    
        if (!Object.keys(conditions).includes(fieldName)) {
            throw new Error("Invalid field name.");
        }

        const homeBases = [...new Set(crewRoutes.map(route => route.dep))].sort();
        const objValues = Object.values(crewRoutes).reduce((acc, cur) => {
            const key = cur[groupBy];
            if (!key) return acc;
            
            const value = cur[fieldName]; 
            const days = cur['routeDays']; 
            const dep = cur.dep;
            
            if ((groupBy === 'dep' && !homeBases.includes(dep)) || days === 0)
                return acc;
            
            if (conditions[fieldName](value)) {            
                if (!acc[key]) {
                    acc[key] = { 
                        totalRoute: 0, 
                        totalTime: 0,
                        totalDays: 0,
                    };

                    homeBases.forEach(base => {
                        acc[key][`${base}_totalRoute`] = 0; 
                        acc[key][`${base}_totalTime`] = 0; 
                        acc[key][`${base}_totalDays`] = 0;
                    });
                }

                if (days > 0) {
                    acc[key].totalRoute += 1;
                    acc[key].totalTime += value;
                    acc[key].totalDays += days;

                    if (homeBases.includes(dep)) {
                        acc[key][`${dep}_totalRoute`] += 1; 
                        acc[key][`${dep}_totalTime`] += value; 
                        acc[key][`${dep}_totalDays`] += days; 
                    }
                }
            }
        
            return acc; 
        }, {});
        
        if (Object.keys(objValues).length === 0) return [];
        
        const arrValues = Object.entries(objValues)
        .map(([key, { totalTime, totalDays }]) => ({
            x: key,
            y: totalDays > 0 ? parseFloat((totalTime / totalDays).toFixed(2)) : 0,
        }))
        .sort((a, b) => ['routeDate', 'routeDateLocalBase'].includes(fieldName)
            ? new Date(a.x) - new Date(b.x)  
            : a.x.localeCompare(b.x)
        );

        const arrValuesByPort = Object.entries(objValues).map(([key, { totalRoute, totalTime, totalDays, ...baseRoutes }]) => {
            const baseData = {};
            
            homeBases.forEach(dep => {            
                baseData[dep] = baseRoutes[`${dep}_totalDays`] > 0 ? parseFloat((baseRoutes[`${dep}_totalTime`] / baseRoutes[`${dep}_totalDays`]).toFixed(2)) : 0;
            });
            
            return {
                x: key,
                ...baseData, 
            };
        }).sort((a, b) => a.x.localeCompare(b.x));

        const radialBarChartData = Object.entries(objValues).map(([key, { totalRoute, totalTime, totalDays, ...baseRoutes }]) => {
            const data = [];
        
            homeBases.forEach(dep => {            
                const value = {
                    x: dep,
                    y: baseRoutes[`${dep}_totalDays`] > 0 ? parseFloat((baseRoutes[`${dep}_totalTime`] / baseRoutes[`${dep}_totalDays`]).toFixed(2)) : 0,
                }
                data.push(value)
            });
    
            return {
                id: key,
                data, 
            };
        });
        
        // const totalRoutes = Object.values(objValues).reduce((acc, { totalRoute }) => acc + totalRoute, 0);
        const totalTimes = Object.values(objValues).reduce((acc, { totalTime }) => acc + totalTime, 0);
        const totalDays = Object.values(objValues).reduce((acc, { totalDays }) => acc + totalDays, 0);
        
        const averageTime = totalDays > 0 ? parseFloat((totalTimes / totalDays).toFixed(2)) : 0;
        const arrAverageTime = arrValues.map(({ x }) => ({
            x,
            y: averageTime,
        }));

        
        const barLineChartData = [        
            { id: 'BH per Day', color: 'hsl(120, 70%, 50%)', data: arrValues },        
            { id: 'Average', color: 'hsl(200, 70%, 50%)', data: arrAverageTime },
        ];
        
        switch (chartType) {
            case 'bar':
            case 'column':
                return arrValues;
            case 'groupedColumn':
                return arrValuesByPort;
            case 'pie':            
                return Object.entries(objValues).map(([key, { totalTime }]) => ({
                    id: key,
                    label: key,
                    value: totalTime,
                }));
            case 'radialbar':
                return radialBarChartData;
            case 'barline':
                return barLineChartData;
            default:
                return barLineChartData;            
        }
    } catch (error) {
        console.error("Error in countBHCrewRoutesByField:", error);
        return []; // Return an empty array as a fallback
    }
}

export function countWeightedBHCrewRoutesByField(crewRoutes, fieldName, groupBy, chartType) {
    try {
        if (!crewRoutes) return [];

        const conditions = {
            'blockHours': (time) => time && time > 0,
            'dhcTime': (time) => time && time > 0,
        };

        if (!Object.keys(conditions).includes(fieldName)) {
            throw new Error("Invalid field name.");
        }

        const homeBases = [...new Set(crewRoutes.map(route => route.dep))].sort();
        const objValues = Object.values(crewRoutes).reduce((acc, cur) => {
            const key = cur[groupBy];
            if (!key) return acc;

            const value = cur[fieldName];
            const days = cur['routeDays'];
            const dep = cur.dep;
            const crewCount = cur.crewCount || 1;

            if ((groupBy === 'dep' && !homeBases.includes(dep)) || days === 0)
                return acc;

            if (conditions[fieldName](value)) {
                if (!acc[key]) {
                    acc[key] = {
                        totalRoute: 0,
                        totalTime: 0,
                        totalDays: 0,
                        totalWeightedTime: 0,
                        totalWeightedDays: 0,
                    };

                    homeBases.forEach(base => {
                        acc[key][`${base}_totalRoute`] = 0;
                        acc[key][`${base}_totalTime`] = 0;
                        acc[key][`${base}_totalDays`] = 0;
                    });
                }

                if (days > 0) {
                    acc[key].totalRoute += 1;
                    acc[key].totalTime += value;
                    acc[key].totalDays += days;
                    acc[key].totalWeightedTime += value * crewCount;
                    acc[key].totalWeightedDays += days * crewCount;

                    if (homeBases.includes(dep)) {
                        acc[key][`${dep}_totalRoute`] += 1;
                        acc[key][`${dep}_totalTime`] += value;
                        acc[key][`${dep}_totalDays`] += days;
                    }
                }
            }

            return acc;
        }, {});

        if (Object.keys(objValues).length === 0) return [];

        const arrValues = Object.entries(objValues)
            .map(([key, { totalTime, totalDays }]) => ({
                x: key,
                y: totalDays > 0 ? parseFloat((totalTime / totalDays).toFixed(2)) : 0,
            }))
            .sort((a, b) => ['routeDate', 'routeDateLocalBase'].includes(fieldName)
                ? new Date(a.x) - new Date(b.x)
                : a.x.localeCompare(b.x)
            );

        const arrValuesByPort = Object.entries(objValues).map(([key, { totalRoute, totalTime, totalDays, totalWeightedTime, totalWeightedDays, ...baseRoutes }]) => {
            const baseData = {};
            homeBases.forEach(dep => {
                baseData[dep] = baseRoutes[`${dep}_totalDays`] > 0 ? parseFloat((baseRoutes[`${dep}_totalTime`] / baseRoutes[`${dep}_totalDays`]).toFixed(2)) : 0;
            });
            return { x: key, ...baseData };
        }).sort((a, b) => a.x.localeCompare(b.x));

        const radialBarChartData = Object.entries(objValues).map(([key, { totalRoute, totalTime, totalDays, totalWeightedTime, totalWeightedDays, ...baseRoutes }]) => {
            const data = [];
            homeBases.forEach(dep => {
                data.push({
                    x: dep,
                    y: baseRoutes[`${dep}_totalDays`] > 0 ? parseFloat((baseRoutes[`${dep}_totalTime`] / baseRoutes[`${dep}_totalDays`]).toFixed(2)) : 0,
                });
            });
            return { id: key, data };
        });

        const totalWeightedTimes = Object.values(objValues).reduce((acc, { totalWeightedTime }) => acc + totalWeightedTime, 0);
        const totalWeightedDays  = Object.values(objValues).reduce((acc, { totalWeightedDays  }) => acc + totalWeightedDays, 0);
        const averageTime = totalWeightedDays > 0 ? parseFloat((totalWeightedTimes / totalWeightedDays).toFixed(2)) : 0;
        const arrAverageTime = arrValues.map(({ x }) => ({ x, y: averageTime }));

        const barLineChartData = [
            { id: 'BH per Day', color: 'hsl(120, 70%, 50%)', data: arrValues },
            { id: 'Average', color: 'hsl(200, 70%, 50%)', data: arrAverageTime },
        ];

        switch (chartType) {
            case 'bar':
            case 'column':
                return arrValues;
            case 'groupedColumn':
                return arrValuesByPort;
            case 'pie':
                return Object.entries(objValues).map(([key, { totalTime }]) => ({
                    id: key, label: key, value: totalTime,
                }));
            case 'radialbar':
                return radialBarChartData;
            case 'barline':
                return barLineChartData;
            default:
                return barLineChartData;
        }
    } catch (error) {
        console.error("Error in countWeightedBHCrewRoutesByField:", error);
        return [];
    }
}

export function countRouteBlockHours(crewRoutes, ranks, groupBy, chartType) {    
    const crewBases = new Set();
    crewRoutes.map(route => {
        return ranks.map(rank => {
            let key = `${rank.rankCode}\n${route.dep}`;
            if (groupBy === 'post') {
                key = `${rank.rankCode}`;
            }
            return crewBases.add(key);
        })        
    });

    const objValues = crewRoutes.reduce((acc, cur) => {
        crewBases.forEach(baseKey => {
            if (!acc[baseKey]) {
                acc[baseKey] = { 
                    count: 0, 
                    blockHours: 0 
                };
            }

            ranks.forEach(rank => {
                // Check if the current crew member matches the baseKey (post + base)
                let crewKey = `${rank.rankCode}\n${cur.dep}`;
                if (groupBy === 'post') {
                    crewKey = `${rank.rankCode}`;
                }

                if (crewKey === baseKey && cur[rank.rankCode] > 0) {
                    if (cur.blockHours > 0) {
                        acc[baseKey]['count']++;
                        acc[baseKey]['blockHours'] += cur.blockHours || 0;  
                    }
                }
            })            
        });

        return acc;
    }, {});

    if (Object.keys(objValues).length === 0) return [];

    const arrValues = Object.entries(objValues)
        .filter(([_, { blockHours }]) => blockHours > 0) // Filter out zero block times
        .map(([key, { count, blockHours }]) => ({
            x: key, 
            y: parseFloat(blockHours.toFixed(1)),
        }))
        .sort((a, b) => a.x.localeCompare(b.x));
    
    const totalBlockHours = arrValues.reduce((acc, cur) => acc + cur.y, 0);
    const totalCount = arrValues.length;
    const averageBlockHours = totalCount > 0 ? totalBlockHours / totalCount : 0;

    const arrAverage= arrValues.map(item => ({
        x: item.x, 
        y: parseFloat(averageBlockHours.toFixed(1)), 
    }));

    const barLineChartData = [        
        { id: 'Block Hours', color: 'hsl(120, 70%, 50%)', data: arrValues },        
        { id: 'Average', color: 'hsl(200, 70%, 50%)', data: arrAverage },
    ];
    
    switch (chartType) {
        case 'bar':
        case 'column':
            return arrValues;        
        case 'pie':            
            return Object.entries(objValues).map(([key, { blockHours }]) => ({
                id: key,
                label: key,
                value: blockHours,
            }));
        case 'barline':
            return barLineChartData;
        default:
            return barLineChartData;            
    }
}

function buildHistogramData(data, fieldName, binSize = 5) {
    const values = Array.from(data.values()).map(m => m[fieldName]);
    if (values.length === 0) return [];

    const min = Math.min(...values);
    const max = Math.max(...values);

    const formatWithLeadingZero = (n, width = 0) =>
        String(n).padStart(width, "0");

    // chuẩn bị bins
    const bins = [];
    for (let b = Math.floor(min/binSize)*binSize; b <= max; b += binSize) {
        bins.push({
            range: `${formatWithLeadingZero(b)} - ${formatWithLeadingZero(b + binSize)}`,
            count: 0,
        });
    }

    // đếm vào bins
    values.forEach(h => {
        const binIndex = Math.floor((h - Math.floor(min/binSize)*binSize) / binSize);
        if (bins[binIndex]) bins[binIndex].count++;
    });

    return bins.map(b => ({
        x: b.range,   // nhãn trục X (khoảng totalHours)
        y: b.count    // số lượng crew trong khoảng
    }));
}

export function countCrewBlockHours(
    crewBlockHours, 
    groupBy = 'post', 
    minHours = 0, 
    chartType = 'barline', 
    isAdjusted = false,
) {
    if (!crewBlockHours || crewBlockHours.length === 0) return [];
    
    // --- Step 1: group data ---
    const objValues = crewBlockHours.reduce((acc, cur) => {
        let key;
        switch (groupBy) {
            case 'post': 
                key = cur.post; 
                break;
            case 'base': 
                key = cur.base; 
                break;
            case 'postbase': 
                key = `${cur.post} ${cur.base}`; 
                break;
            default: 
                key = cur.crewCode; 
        }

        if (!acc[key]) {
            acc[key] = { 
                count: 0, 
                blockHours: 0, 
                totalHours: 0, 
                members: [], 
            };
        }
        if (cur.totalHours > minHours) {
            acc[key].count++;

            if (isAdjusted) {   
                acc[key].blockHours += cur.adjustedBlockHours || 0;
                acc[key].totalHours += cur.adjustedTotalHours || 0;
            } else {
                acc[key].blockHours += cur.blockHours || 0;
                acc[key].totalHours += cur.totalHours || 0;
            }
            acc[key].members.push(cur);
        }

        return acc;
    }, {});

    if (Object.keys(objValues).length === 0) return [];

    // --- Step 2: aggregate values ---
    const arrValues = Object.entries(objValues)
        .map(([key, { count, totalHours }]) => ({
            x: key,
            y: count > 0 ? parseFloat((totalHours / count).toFixed(1)) : 0, // trung bình totalHours
        }))
        .sort((a, b) => a.x.localeCompare(b.x));

    // --- Step 3: compute overall average ---
    const sumTotalHours = Object.values(objValues).reduce((acc, { totalHours }) => acc + totalHours, 0);
    const totalCount = Object.values(objValues).reduce((acc, { count }) => acc + count, 0);
    const avgTotalHours = totalCount > 0 ? sumTotalHours / totalCount : 0;

    const arrAverage = arrValues.map(item => ({
        x: item.x,
        y: parseFloat(avgTotalHours.toFixed(1)),
    }));

    const barLineChartData = [
        { id: 'Block Hours', color: 'hsl(120, 70%, 50%)', data: arrValues },
        { id: 'Average', color: 'hsl(200, 70%, 50%)', data: arrAverage },
    ];

    const scatterplotData = Object.entries(objValues).map(([groupKey, { members }]) => ({
        id: groupKey,
        data: members.map(m => ({
            x: m.sectors,
            y: m.totalHours,
            avg: m.avgTotalHours,
            base: m.base,
            post: m.post,
        }))
    }));

    const histogramData = buildHistogramData(crewBlockHours, 'totalHours', 10);
    // const histogramAvgBH = histogramData.map(item => ({
    //     x: item.x,
    //     y: parseFloat(avgTotalHours.toFixed(1)),
    // }));
    // const histogramChartData = [
    //     { id: 'No. of Crew', color: 'hsl(120, 70%, 50%)', data: histogramData },
    //     { id: 'AvgBH', color: 'hsl(200, 70%, 50%)', data: histogramAvgBH },
    // ];
    const histogramChartData = histogramData;

    // --- Step 4: return based on chart type ---
    switch (chartType) {
        case 'bar':
        case 'column':
            return arrValues;
        case 'pie':
            return Object.entries(objValues).map(([key, { totalHours }]) => ({
                id: key,
                label: key,
                value: totalHours,
            }));
        case 'scatterplot':
            return scatterplotData;
        case 'histogram':
            return histogramChartData;
        case 'barline':
            return barLineChartData;
        default:
            return barLineChartData;
    }
}

export function countCrewTotalHours(crewTotalHours, groupBy, minTotalHours = 0, chartType) {
    if (!crewTotalHours || crewTotalHours.length === 0) return [];

    // Gom nhóm trực tiếp
    const objValues = crewTotalHours.reduce((acc, cur) => {
        let key;
        switch (groupBy) {
            case 'post':
                key = cur.post;
                break;
            case 'base':
                key = cur.base;
                break;
            case 'postbase':
                key = `${cur.post} ${cur.base}`;
                break;
            default:
                key = cur.crewCode;
        }

        if (!acc[key]) {
            acc[key] = {
                count: 0,
                totalHours: 0,
                blockHours: 0,
                simHours: 0,
                members: [], 
            };
        }

        if (cur.totalHours > minTotalHours) {
            acc[key].count++;
            acc[key].totalHours += cur.totalHours || 0;
            acc[key].blockHours += cur.blockHours || 0;
            acc[key].simHours += cur.simHours || 0;
            acc[key].members.push(cur);
        }

        return acc;
    }, {});

    if (Object.keys(objValues).length === 0) return [];

    // Bar/column values (avg totalHours)
    const arrValues = Object.entries(objValues)
        .map(([key, { count, totalHours }]) => ({
            x: key,
            y: count > 0 ? parseFloat((totalHours / count).toFixed(1)) : 0,
        }))
        .sort((a, b) => a.x.localeCompare(b.x));

    // Average line
    const sumTotalHours = Object.values(objValues).reduce((acc, { totalHours }) => acc + totalHours, 0);
    const totalCount = Object.values(objValues).reduce((acc, { count }) => acc + count, 0);
    const avgTotalHours = totalCount > 0 ? sumTotalHours / totalCount : 0;

    const arrAverage = arrValues.map(item => ({
        x: item.x,
        y: parseFloat(avgTotalHours.toFixed(1)),
    }));

    const barLineChartData = [
        { id: 'Total Hours', color: 'hsl(120, 70%, 50%)', data: arrValues },
        { id: 'Average', color: 'hsl(200, 70%, 50%)', data: arrAverage },
    ];

    // Stack chart: block vs sim
    const stackChartData = Object.entries(objValues)
        .map(([key, { count, blockHours, simHours }]) => ({
            x: key,
            BlockHours: count > 0 ? parseFloat((blockHours / count).toFixed(1)) : 0,
            SimHours: count > 0 ? parseFloat((simHours / count).toFixed(1)) : 0,
        }))
        .sort((a, b) => a.x.localeCompare(b.x));

        
    const scatterplotData = Object.entries(objValues).map(([groupKey, { members }]) => ({
        id: groupKey,
        data: members.map(m => ({
            x: m.sectors,
            y: m.totalHours,
            avg: m.avgTotalHours,
            base: m.base,
            post: m.post,
        }))
    }));

    const histogramData = buildHistogramData(crewTotalHours, 'totalHours', 10);
    // const histogramAvgBH = histogramData.map(item => ({
    //     x: item.x,
    //     y: parseFloat(avgTotalHours.toFixed(1)),
    // }));
    // const histogramChartData = [
    //     { id: 'No. of Crew', color: 'hsl(120, 70%, 50%)', data: histogramData },
    //     { id: 'AvgBH', color: 'hsl(200, 70%, 50%)', data: histogramAvgBH },
    // ];
    const histogramChartData = histogramData;

    // Trả kết quả theo chartType
    switch (chartType) {
        case 'bar':
        case 'column':
            return arrValues;
        case 'pie':
            return Object.entries(objValues).map(([key, { totalHours }]) => ({
                id: key,
                label: key,
                value: totalHours,
            }));
        case 'stack':
            return stackChartData;
        case 'scatterplot':
            return scatterplotData;
        case 'histogram':
            return histogramChartData;
        case 'barline':
        default:
            return barLineChartData;
    }
}

export function countFlyingCrew(crewMembers, groupBy, chartType) {
    const objValues = crewMembers.reduce((acc, cur) => {
        let key;
        switch (groupBy) {
            case 'post': 
                key = cur.post; 
                break;
            case 'base': 
                key = cur.base; 
                break;
            case 'postbase': 
                key = `${cur.post} ${cur.base}`; 
                break;
            default: 
                key = cur.crewCode; 
        }
        if (!acc[key]) {
            acc[key] = { 
                count: 0, 
            };
        }

        acc[key].count++;
        
        return acc;
    }, {});

    if (Object.keys(objValues).length === 0) return [];

    const arrValues = Object.entries(objValues).map(([key, { count }]) => ({
        x: key,
        y: count || 0,
    })).sort((a, b) => a.x.localeCompare(b.x));
    
    const totalCount = Object.values(objValues).reduce((acc, { count }) => acc + count, 0);
    const average = arrValues ? totalCount / arrValues.length : 0;

    const arrAverage= arrValues.map(item => ({
        x: item.x, 
        y: parseFloat(average.toFixed(0)), 
    }));

    const barLineChartData = [        
        { id: 'CrewCount', color: 'hsl(120, 70%, 50%)', data: arrValues },        
        { id: 'Average', color: 'hsl(200, 70%, 50%)', data: arrAverage },
    ];
    
    switch (chartType) {
        case 'bar':
        case 'column':
            return arrValues;        
        case 'pie':            
            return Object.entries(objValues).map(([key, { count }]) => ({
                id: key,
                label: key,
                value: count,
            }));
        case 'barline':
            return barLineChartData;
        default:
            return barLineChartData;            
    }
}

export function countRosterDetailsByField(rosterDetails, groupBy, chartType, days = 1) {  
    const homeBases = [...new Set(rosterDetails.map(roster => roster.dep))].sort();
    const objValues = Object.values(rosterDetails).reduce((acc, cur) => {
        const key = cur[groupBy] ?? 'N/A';
        const dep = cur.dep;
        
        if (groupBy === 'dep' && !homeBases.includes(dep))
            return acc;
        
        if (!acc[key]) {
            acc[key] = { 
                total: 0, 
            };
               
            homeBases.forEach(base => {
                acc[key][base] = 0; 
            });
        }

        acc[key].total += 1; 
        
        if (homeBases.includes(dep)) {
            acc[key][dep] += 1; 
        }
    
        return acc; 
    }, {});
    
    if (Object.keys(objValues).length === 0) return [];
    
    // Sorting function
    const sortData = (a, b) => 
        ['flightDate', 'flightDateLocal'].includes(groupBy)
            ? new Date(a.x) - new Date(b.x)  
            : a.x.localeCompare(b.x);

    // Create dataset arrays  
    const arrRosters = Object.entries(objValues)
        .map(([key, { total }]) => ({
            x: key,
            y: total / days,
        }))
        .sort(sortData);

    const totalCounts = Object.values(objValues).reduce((acc, cur) => acc + 1, 0);
    const totalAllRosters = Object.values(objValues).reduce((acc, { total }) => acc + total, 0);
        
    const rostersAverage = totalAllRosters > 0 ? totalAllRosters/ totalCounts : 0;
    const arrRoutesAverage = arrRosters.map(({ x }) => ({
        x,
        y: rostersAverage / days,
    }));
    
    const barLinesChartData = [        
        { id: 'Routes', color: 'hsl(120, 70%, 50%)', data: arrRosters },        
        { id: 'RoutesAvg', color: 'hsl(200, 70%, 50%)', data: arrRoutesAverage },        
    ];
    
    const arrRostersByDep = Object.entries(objValues).map(([key, { total, ...baseCount }]) => {
        const baseData = {};
    
        homeBases.forEach(base => {
            baseData[base] = (baseCount[base] || 0) / days; 
        });
        
        return {
            x: key,
            ...baseData, 
        };
    }).sort((a, b) => a.x.localeCompare(b.x));
    
    const radialBarChartData = Object.entries(objValues).map(([key, { total, ...baseCount }]) => {
        const data = [];
    
        homeBases.forEach(base => {            
            const value = {
                x: base,
                y: (baseCount[base] || 0) / days,
            }
            data.push(value)
        });

        return {
            id: key,
            data, 
        };
    });

    switch (chartType) {
        case 'bar':
        case 'column':
            return arrRosters;
        case 'groupedColumn':
            return arrRostersByDep;
        case 'pie':            
            return Object.entries(objValues).map(([key, {total}]) => ({
                id: key,
                label: key,
                value: total,
            }));
        case 'radialbar':
            return radialBarChartData;
        case 'barlines':
            return barLinesChartData;
        default:
            return barLinesChartData;            
    }
}

export function countRosterDetails(rosterDetails, groupBy, chartType) {
    const objValues = rosterDetails.reduce((acc, cur) => {
        let key;
        switch (groupBy) {
            case 'post': 
                key = cur.post; 
                break;
            case 'base': 
                key = cur.base; 
                break;
            case 'postbase': 
                key = `${cur.post} ${cur.base}`; 
                break;
            default: 
                key = cur.crewCode; 
        }
        if (!acc[key]) {
            acc[key] = { 
                count: 0, 
            };
        }

        acc[key].count++;
        
        return acc;        
    }, {});

    if (Object.keys(objValues).length === 0) return [];

    const arrValues = Object.entries(objValues).map(([key, { count }]) => ({
        x: key,
        y: count || 0,
    })).sort((a, b) => a.x.localeCompare(b.x));
    
    const totalCount = Object.values(objValues).reduce((acc, { count }) => acc + count, 0);
    const average = arrValues ? totalCount / arrValues.length : 0;

    const arrAverage= arrValues.map(item => ({
        x: item.x, 
        y: parseFloat(average.toFixed(0)), 
    }));

    const barLineChartData = [        
        { id: 'Days', color: 'hsl(120, 70%, 50%)', data: arrValues },        
        { id: 'Average', color: 'hsl(200, 70%, 50%)', data: arrAverage },
    ];
    
    switch (chartType) {
        case 'bar':
        case 'column':
            return arrValues;        
        case 'pie':            
            return Object.entries(objValues).map(([key, { count }]) => ({
                id: key,
                label: key,
                value: count,
            }));
        case 'barline':
            return barLineChartData;
        default:
            return barLineChartData;            
    }
}
/* ---------------- End of Crew Charts -----------------*/

/* ------------------ Compare Charts -------------------*/
export function compareFlights(flightObjects, chartType, legend = 'Flt', legendAvg = 'FltAvg', yValue = 'count') {  
    const obj = Object.entries(flightObjects).reduce((acc, [key, value]) => {
        if (!acc[key]) {            
            acc[key] = { 
                count: 0, 
                totalBlockHours: 0, 
                blockMinutes: 0, 
                btDiff: 0,
                airMinutes: 0,
                depOtp: 100, 
                arrOtp: 100, 
                depDelayMinutes: 0,
                arrDelayMinutes: 0,
                loadFactor: 0,
                pax: 0,
                tat: 0,
                taxiOut: 0,
                taxiIn: 0,
            };
        }
               
        let depDelayedCount = 0;
        let depOtpCount = 0;
        let arrDelayedCount = 0;
        let arrOtpCount = 0;

        let totalDepDelayMinutes = 0;
        let totalArrDelayMinutes = 0;

        let totalPax = 0;
        let totalCapacity = 0;

        let totalBlockHours = 0;
        let totalBlockMinutes = 0;

        let btDiffCount = 0;
        let totalBTDiff = 0;

        let airMinutesCount = 0;
        let totalAirMinutes = 0;

        let tatCount = 0;
        let totalTAT = 0;
        let totalTATDiff = 0;

        let taxiOutCount = 0;
        let totalTaxiOut = 0;

        let taxiInCount = 0;
        let totalTaxiIn = 0;

        
        value.forEach(f => {
            const flightStatus = f.flightStatus?.toLowerCase();
                            
            if (flightStatus !== 'not yet departed') {
                depOtpCount++;
                if (f.depDelaysTime > OTP_DELAY_MINUTES) {
                    depDelayedCount++;
                    totalDepDelayMinutes += f.depDelaysTime || 0;
                }
            }

            if (flightStatus === 'arrived') {
                arrOtpCount++;
                if (f.arrDelaysTime > OTP_DELAY_MINUTES) {
                    arrDelayedCount++;
                    totalArrDelayMinutes += f.arrDelaysTime || 0;
                }
            }
            
            if (f.tat < MAXIMUM_TAT_MINUTES) {
                tatCount += 1;
                totalTAT += f.tat || 0;
                totalTATDiff += f.tatDiff || 0;
            }

            if (f.taxiOut > 0) {
                taxiOutCount += 1;
                totalTaxiOut += f.taxiOut || 0;
            }

            if (f.taxiIn > 0) {
                taxiInCount += 1;
                totalTaxiIn += f.taxiIn || 0;
            }
            
            totalBlockHours += f.blockHours || 0;
            totalBlockMinutes += f.blockMinutes || 0;

            if (f.atd && f.ata) {
                btDiffCount += 1;
                totalBTDiff += f.btDiff || 0;
            }

            if (f.takeOff && f.touchDown) {
                airMinutesCount += 1;
                totalAirMinutes += f.airMinutes || 0;
            }

            const noOfPax = Number(f.noOfPax) || 0;
            const capacity = Number(f.capacity) || 0;
            totalPax += noOfPax;
            if (noOfPax > 0) {
                totalCapacity +=  capacity; 
            }            
        });        
                
        acc[key].count = value.length;
        acc[key].totalBlockHours = totalBlockHours;
        acc[key].blockMinutes = value.length && totalBlockMinutes / value.length;
        acc[key].btDiff = btDiffCount > 0 ? (totalBTDiff / btDiffCount) : 0;
        acc[key].airMinutes = airMinutesCount > 0 ? (totalAirMinutes / airMinutesCount) : 0;
        
        acc[key].depOtp = depOtpCount > 0 ? ((1 - depDelayedCount / depOtpCount) * 100) : 100;
        acc[key].arrOtp = arrOtpCount > 0 ? ((1 - arrDelayedCount / arrOtpCount) * 100) : 100;

        acc[key].depDelayMinutes = depDelayedCount > 0 ? (totalDepDelayMinutes / depDelayedCount) : 0;
        acc[key].arrDelayMinutes = arrDelayedCount > 0 ? (totalArrDelayMinutes / arrDelayedCount) : 0;
        
        acc[key].loadFactor = totalCapacity > 0 ? (totalPax / totalCapacity * 100) : 0;
        acc[key].pax = totalPax || 0;
        
        acc[key].tat = tatCount > 0 ? (totalTAT / tatCount) : 0;
        acc[key].tatDiff = tatCount > 0 ? (totalTATDiff / tatCount) : 0;
        acc[key].taxiOut = taxiOutCount > 0 ? (totalTaxiOut / taxiOutCount) : 0;
        acc[key].taxiIn = taxiInCount > 0 ? (totalTaxiIn / taxiInCount) : 0;
        
        return acc;
    }, {});
    
    if (Object.keys(obj).length === 0) return [];
    
    const arrFlights = Object.entries(obj)
        .map(([key, value]) => ({
            x: key, 
            y: value[yValue],
        }));    
    
    const flightsTotal = arrFlights.reduce((acc, cur) => acc + cur.y, 0);
    const fightsAverage = parseFloat((flightsTotal / arrFlights.length).toFixed(1));       
    const arrFlightsAverage = arrFlights.map(({ x }) => ({
        x,
        y: fightsAverage
    }));    
    
    const barLineChartData = [        
        { id: legend, color: 'hsl(120, 70%, 50%)', data: arrFlights },        
        { id: legendAvg, color: 'hsl(200, 70%, 50%)', data: arrFlightsAverage },
    ];
    
    switch (chartType) {
        case 'bar':
        case 'column':
            return arrFlights;        
        case 'pie':            
        return Object.entries(obj).map(([key, value]) => {
            return {
                id: key, 
                label: key, 
                value: value[yValue],
            };
        });        
        case 'barline':
            return barLineChartData;
        default:
            return barLineChartData;            
    }
}
/* --------------- End of Compare Charts ---------------*/

/* ---------------- Crew Members Charts ----------------*/
export function countCrewMembers(crewMembers, groupBy, chartType) {
    const crewTypes = new Set();

    // 1. Xây dựng set các loại nhóm
    crewMembers.forEach(member => {
        let key = member[groupBy] || 'N/A';
        if (groupBy === 'post&base' || groupBy === 'stackedgender') {
            key = `${member.post}\n${member.base}`;
        }
        crewTypes.add(key);
    });

    // 2. Tính toán số lượng theo nhóm
    const objValues = crewMembers.reduce((acc, cur) => {
        let key = cur[groupBy] || 'N/A';
        if (groupBy === 'post&base' || groupBy === 'stackedgender') {
            key = `${cur.post}\n${cur.base}`;
        }

        if (!acc[key]) {
            acc[key] = {
                count: 0,
                Male: 0,
                Female: 0,
            };
        }

        acc[key].count++;

        // Gộp số lượng giới tính nếu là stackedbar
        if (groupBy === 'stackedgender') {
            const gender = cur.gender;
            if (gender === 'ML') 
                acc[key].Male++;
            else if (gender === 'FM') 
                acc[key].Female++;
        }

        return acc;
    }, {});

    if (Object.keys(objValues).length === 0) return [];

    // 3. Tính dữ liệu
    const arrValues = Object.entries(objValues).map(([key, { count }]) => ({
        x: key,
        y: count || 0,
    })).sort((a, b) => a.x.localeCompare(b.x));

    const totalCount = Object.values(objValues).reduce((acc, { count }) => acc + count, 0);
    const average = arrValues.length ? totalCount / arrValues.length : 0;

    const arrAverage = arrValues.map(item => ({
        x: item.x,
        y: parseFloat(average.toFixed(0)),
    }));

    const barLineChartData = [
        { id: 'CrewCount', color: 'hsl(120, 70%, 50%)', data: arrValues },
        { id: 'Average', color: 'hsl(200, 70%, 50%)', data: arrAverage },
    ];

    // 4. Dữ liệu cho stackedbar
    const stackedChartData = Object.entries(objValues).map(([key, value]) => ({
        x: key,
        Male: value.Male,
        Female: value.Female,
    }));

    // 5. Trả kết quả
    switch (chartType) {
        case 'bar':
        case 'column':
            return arrValues;
        case 'pie':
            return Object.entries(objValues).map(([key, { count }]) => ({
                id: key,
                label: key,
                value: count,
            }));
        case 'stackedbar':
            return stackedChartData;
        case 'barline':
        default:
            return barLineChartData;
    }
}

export function countAllCrewMembers(allCrewMembers, groupBy, chartType) {
    if (!Array.isArray(allCrewMembers)) return [];

    const objValues = allCrewMembers.reduce((acc, cur) => {
        let key;
        switch (groupBy) {
            case 'post': 
                key = cur.post; 
                break;
            case 'base': 
                key = cur.base; 
                break;
            case 'postbase': 
                key = `${cur.post} ${cur.base}`; 
                break;
            default: 
                key = cur.crewCode; 
        }

        const type = cur.isFlying ? 'flying' : 'nonflying';

        if (!key) return acc;

        if (!acc[key]) {
            acc[key] = { nonflying: 0, flying: 0 };
        }

        acc[key][type] += 1;
        return acc;
    }, {});

    if (Object.keys(objValues).length === 0) return [];

    const totalFlying = Object.values(objValues).reduce((sum, v) => sum + v.flying, 0);
    const totalNonFlying = Object.values(objValues).reduce((sum, v) => sum + v.nonflying, 0);

    const stackedChartData = Object.entries(objValues).map(([key, value]) => ({
        x: key,
        All: (value.flying + value.nonflying),
        Flying: value.flying,
    })).sort((a, b) => a.x.localeCompare(b.x));

    switch (chartType) {        
        case 'pie':
            return [
                { id: "Flying", label: "Flying", value: totalFlying },
                { id: "Non-Flying", label: "Non-Flying", value: totalNonFlying }
            ];
        case 'stackedbar':
            return stackedChartData;
        default:
            return stackedChartData;
    }
}
/* ------------- End of Crew Members Charts ------------*/

/* ------------------- Flight Tableau --------------------*/
export function countAvgMinutesByTableau(flights, field1, field2, minutesField, rowLabel = 'Block Minutes', condition = null) {
    if (!Array.isArray(flights) || flights.length === 0) return [];

    const objValues = flights.reduce((acc, flight) => {
        if (flight.dep === flight.arr) return acc;

        const f1 = flight[field1];
        if (f1 === undefined || f1 === null) return acc;
        const key = field2
            ? `${f1}|${flight[field2] ?? 'N/A'}`
            : `${f1}|${rowLabel}`;

        if (!acc[key]) {
            acc[key] = { totalFlt: 0, totalValue: 0, flights: [] };
        }
        const val = Number(flight[minutesField]) || 0;
        if (condition && !condition(flight, val)) return acc;
        acc[key].totalFlt += 1;
        acc[key].totalValue += val;
        acc[key].flights.push({
            flightNo: flight.flightNo,
            flightDate: flight.flightDate,
            flightDateLocalBase: flight.flightDateLocalBase,
            sector: flight.sector,
            std: flight.std,
            sta: flight.sta,
            etd: flight.etd,
            eta: flight.eta,
            atd: flight.atd,
            ata: flight.ata,
            stdLocalBase: flight.stdLocalBase,
            staLocalBase: flight.staLocalBase,
            etdLocalBase: flight.etdLocalBase,
            etaLocalBase: flight.etaLocalBase,
            atdLocalBase: flight.atdLocalBase,
            ataLocalBase: flight.ataLocalBase,
            val: Math.round(val),
            flightStatus: flight.flightStatus,
        });

        return acc;
    }, {});

    if (Object.keys(objValues).length === 0) return [];

    return Object.entries(objValues)
        .map(([key, { totalFlt, totalValue, flights }]) => ({
            key,
            avgValue: totalFlt > 0 ? parseFloat((totalValue / totalFlt).toFixed(1)) : 0,
            totalValue: parseFloat(totalValue.toFixed(1)),
            totalFlt,
            flights,
        }))
        .sort((a, b) => {
            const [partA] = a.key.split('|');
            const [partB] = b.key.split('|');
            const dateA = Date.parse(partA);
            const dateB = Date.parse(partB);
            if (!isNaN(dateA) && !isNaN(dateB)) return dateA - dateB;
            return a.key.localeCompare(b.key);
        });
}

export function countBlockHoursByTableau(flights, field1, field2, blockHoursField = 'blockHours') {
    if (!Array.isArray(flights) || flights.length === 0) return [];

    const objValues = flights.reduce((acc, flight) => {
        const f1 = flight[field1];
        if (f1 === undefined || f1 === null) return acc;
        const key = field2
            ? `${f1}|${flight[field2] ?? 'N/A'}`
            : `${f1}|Block Hours`;

        if (!acc[key]) {
            acc[key] = { totalBH: 0, flights: [] };
        }
        const bh = Number(flight[blockHoursField]) || 0;
        acc[key].totalBH += bh;
        acc[key].flights.push({
            flightNo: flight.flightNo,
            flightDate: flight.flightDate,
            flightDateLocalBase: flight.flightDateLocalBase,
            sector: flight.sector,
            std: flight.std,
            sta: flight.sta,
            etd: flight.etd,
            eta: flight.eta,
            atd: flight.atd,
            ata: flight.ata,
            stdLocalBase: flight.stdLocalBase,
            staLocalBase: flight.staLocalBase,
            etdLocalBase: flight.etdLocalBase,
            etaLocalBase: flight.etaLocalBase,
            atdLocalBase: flight.atdLocalBase,
            ataLocalBase: flight.ataLocalBase,
            bh: parseFloat(bh.toFixed(2)),
            flightStatus: flight.flightStatus,
        });

        return acc;
    }, {});

    if (Object.keys(objValues).length === 0) return [];

    return Object.entries(objValues)
        .map(([key, { totalBH, flights }]) => ({
            key,
            totalBH: parseFloat(totalBH.toFixed(1)),
            flights,
        }))
        .sort((a, b) => {
            const [partA] = a.key.split('|');
            const [partB] = b.key.split('|');
            const dateA = Date.parse(partA);
            const dateB = Date.parse(partB);
            if (!isNaN(dateA) && !isNaN(dateB)) return dateA - dateB;
            return a.key.localeCompare(b.key);
        });
}

export function countFlightsByTableau(flights, field1, field2) {
    if (!Array.isArray(flights) || flights.length === 0) return [];

    const objValues = flights.reduce((acc, flight) => {
        const f1 = flight[field1];
        if (f1 === undefined || f1 === null) return acc;
        const key = field2
            ? `${f1}|${flight[field2] ?? 'N/A'}`
            : `${f1}|Flights`;

        if (!acc[key]) {
            acc[key] = { count: 0, flights: [] };
        }
        acc[key].count += 1;
        acc[key].flights.push({
            flightNo: flight.flightNo,
            flightDate: flight.flightDate,
            flightDateLocalBase: flight.flightDateLocalBase,
            sector: flight.sector,
            std: flight.std,
            sta: flight.sta,
            etd: flight.etd,
            eta: flight.eta,
            atd: flight.atd,
            ata: flight.ata,
            stdLocalBase: flight.stdLocalBase,
            staLocalBase: flight.staLocalBase,
            etdLocalBase: flight.etdLocalBase,
            etaLocalBase: flight.etaLocalBase,
            atdLocalBase: flight.atdLocalBase,
            ataLocalBase: flight.ataLocalBase,
            flightStatus: flight.flightStatus,
        });

        return acc;
    }, {});

    if (Object.keys(objValues).length === 0) return [];

    return Object.entries(objValues)
        .map(([key, { count, flights }]) => ({ key, count, flights }))
        .sort((a, b) => {
            const [partA] = a.key.split('|');
            const [partB] = b.key.split('|');
            const dateA = Date.parse(partA);
            const dateB = Date.parse(partB);
            if (!isNaN(dateA) && !isNaN(dateB)) return dateA - dateB;
            return a.key.localeCompare(b.key);
        });
}

/* ------------------- Generic Item Tableau --------------------*/
export function countItemsByTableau(items, field1, field2, label = 'Count') {
    if (!Array.isArray(items) || items.length === 0) return [];

    const acc = {};
    for (const item of items) {
        const f1 = item[field1];
        if (f1 === undefined || f1 === null) continue;
        const key = field2
            ? `${f1}|${item[field2] ?? 'N/A'}`
            : `${f1}|${label}`;
        if (!acc[key]) acc[key] = { count: 0 };
        acc[key].count += 1;
    }

    return Object.entries(acc)
        .map(([key, { count }]) => ({ key, count, flights: [] }))
        .sort((a, b) => {
            const [partA] = a.key.split('|');
            const [partB] = b.key.split('|');
            const dateA = Date.parse(partA);
            const dateB = Date.parse(partB);
            if (!isNaN(dateA) && !isNaN(dateB)) return dateA - dateB;
            return a.key.localeCompare(b.key);
        });
}

export function countBHByTableau(items, field1, field2, bhField = 'blockHours', label = 'BH', daysField = null) {
    if (!Array.isArray(items) || items.length === 0) return [];

    const acc = {};
    for (const item of items) {
        const f1 = item[field1];
        if (f1 === undefined || f1 === null) continue;
        const key = field2
            ? `${f1}|${item[field2] ?? 'N/A'}`
            : `${f1}|${label}`;
        if (!acc[key]) acc[key] = { totalBH: 0, totalDays: 0, flights: [] };
        const bh = Number(item[bhField]) || 0;
        acc[key].totalBH += bh;
        if (daysField) acc[key].totalDays += Number(item[daysField]) || 0;
        acc[key].flights.push(item);
    }

    return Object.entries(acc)
        .map(([key, { totalBH, totalDays, flights }]) => {
            const rawBH = parseFloat(totalBH.toFixed(1));
            const displayBH = daysField && totalDays > 0
                ? parseFloat((totalBH / totalDays).toFixed(2))
                : rawBH;
            return { key, totalBH: displayBH, rawBH, totalDays, flights };
        })
        .sort((a, b) => {
            const [partA] = a.key.split('|');
            const [partB] = b.key.split('|');
            const dateA = Date.parse(partA);
            const dateB = Date.parse(partB);
            if (!isNaN(dateA) && !isNaN(dateB)) return dateA - dateB;
            return a.key.localeCompare(b.key);
        });
}